import React, { useMemo, useState, useEffect } from "react";
import { apiService } from "../../services/api";
import Dropdown from "../common/Dropdown";
import FloatingInput from "../auth/FloatingInput";
import { useAppSelector } from "../../store";

type ContactFormProps = {
  className?: string;
  onSubmitted?: () => void;
  heading?: string;
  subheading?: string;
  showHeader?: boolean;
  prefill?: {
    message?: string;
    subject?: string;
  }
  boxed?: boolean; // when false, render without outer card wrapper
};

const ContactForm: React.FC<ContactFormProps> = ({
  className = "",
  onSubmitted,
  heading = "Send us a message",
  subheading = "Fill out the form below and we'll get back to you within 24 hours.",
  showHeader = true,
  boxed = true,
  prefill,
}) => {
  // Get user and company data from Redux store
  const user = useAppSelector((state) => state.auth.user);
  const currentCompany = useAppSelector((state) => state.companies.currentCompany);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    subject: prefill?.subject || "",
    message: prefill?.message || "",
    company: "", // Honeypot
  });
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);

  // Auto-populate form data when user is logged in
  useEffect(() => {
    if (isAuthenticated && user) {
      setFormData((prev) => ({
        ...prev,
        name: `${user.firstName} ${user.lastName}`.trim() || prev.name,
        phone: user.phone || prev.phone,
        email: user.email || prev.email,
      }));
    }
  }, [isAuthenticated, user]);

  // Apply prefill data when provided
  useEffect(() => {
    if (prefill) {
      setFormData((prev) => ({
        ...prev,
        subject: prefill.subject || prev.subject,
        message: prefill.message || prev.message,
      }));
    }
  }, [prefill]);

  const subjectItems = useMemo(
    () => [
      { value: "general", label: "General Inquiry" },
      { value: "support", label: "Technical Support" },
      { value: "sales", label: "Sales Question" },
      { value: "demo", label: "Request a Demo" },
      { value: "partnership", label: "Partnership Opportunity" },
      { value: "feedback", label: "Feedback" },
      { value: "subscription", label: "Subscription Inquiry" },
    ],
    []
  );

  const validatePhone = (phone: string): boolean => {
    // Remove all non-digit characters for validation
    const digitsOnly = phone.replace(/\D/g, '');

    // Accept international format: + followed by 7-15 digits
    // Or accept local format: 10 digits (for India)
    const internationalRegex = /^\+?\d{7,15}$/;
    const localRegex = /^\d{10}$/;

    const isValid = internationalRegex.test(digitsOnly) || localRegex.test(digitsOnly);

    if (!isValid && phone.trim()) {
      setPhoneError('Please enter a valid phone number (10 digits or international format with country code)');
      return false;
    }

    setPhoneError(null);
    return true;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, phone: value }));

    // Validate on blur or when user stops typing
    if (value.trim()) {
      validatePhone(value);
    } else {
      setPhoneError(null);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFeedback(null);
    setSubmitting(true);
    try {
      // Validate phone number before submission
      if (!validatePhone(formData.phone)) {
        setSubmitting(false);
        return;
      }

      // Prepare the base message
      let messageWithContext = formData.message.trim();

      // Append company information if user is logged in and has a company
      if (isAuthenticated && currentCompany) {
        const companyInfo = [
          `\n\n--- System Information ---`,
          `Company: ${currentCompany.name}`,
          `Company ID: ${currentCompany._id}`,
        ];

        // Add Chargebee information if available
        if (currentCompany.chargebee) {
          if (currentCompany.chargebee.customerId) {
            companyInfo.push(`Chargebee Customer ID: ${currentCompany.chargebee.customerId}`);
          }
          if (currentCompany.chargebee.subscriptionId) {
            companyInfo.push(`Chargebee Subscription ID: ${currentCompany.chargebee.subscriptionId}`);
          }
        }

        messageWithContext += companyInfo.join('\n');
      }

      const payload = {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim() || undefined, // optional
        subject: formData.subject,
        message: messageWithContext,
        honeypot: formData.company,
      };

      if (
        !payload.name ||
        !payload.phone ||
        !payload.subject ||
        !formData.message.trim() // Check original message, not the one with appended info
      ) {
        setFeedback("Please fill in all required fields.");
        setSubmitting(false);
        return;
      }

      await apiService.post("/public/leads", payload);
      setFeedback("Thank you! We will get back to you soon.");

      // Reset form - keep user data if logged in
      if (isAuthenticated && user) {
        setFormData({
          name: `${user.firstName} ${user.lastName}`.trim(),
          phone: user.phone || "",
          email: user.email || "",
          subject: "",
          message: "",
          company: "",
        });
      } else {
        setFormData({
          name: "",
          phone: "",
          email: "",
          subject: "",
          message: "",
          company: "",
        });
      }

      onSubmitted?.();
    } catch (err: any) {
      setFeedback(
        err?.response?.data?.error ||
          "Something went wrong. Please try again later."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={className}>
      {boxed ? (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 md:p-10">
          {showHeader && (
            <>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {heading}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-8">
                {subheading}
              </p>
            </>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <FloatingInput
                  id="name"
                  label="Your Name"
                  required
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      name: (e.target as HTMLInputElement).value,
                    }))
                  }
                  placeholder="John Doe"
                />
              </div>

              <div>
                <FloatingInput
                  id="phone"
                  type="tel"
                  label="Phone Number"
                  required
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  placeholder="+91 98765 43210"
                />
                {phoneError && (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                    {phoneError}
                  </p>
                )}
              </div>

              {/* Optional email field (hidden by default on public page) */}
              <div className="hidden">
                <FloatingInput
                  id="email"
                  type="email"
                  label="Email Address"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      email: (e.target as HTMLInputElement).value,
                    }))
                  }
                  placeholder="john@example.com"
                />
              </div>

              {/* Honeypot field - visually hidden */}
              <div className="sr-only" aria-hidden="true">
                <label htmlFor="company">Company</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  autoComplete="off"
                  tabIndex={-1}
                />
              </div>
            </div>

            <div>
              {/* <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Subject *
            </label> */}
              <Dropdown
                items={subjectItems}
                type="single"
                value={formData.subject}
                onChange={(val) =>
                  setFormData((prev) => ({
                    ...prev,
                    subject: String(val || ""),
                  }))
                }
                placeholder="Select a subject"
                required
                showClear={false}
                className="text-left"
              />
            </div>

            <div className="relative floating-group">
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={6}
                placeholder=" "
                className="peer block w-full rounded-xl border bg-white/70 dark:bg-secondary-800/60 dark:border-secondary-600/60 border-secondary-300/80 px-3 pt-6 pb-2 text-sm text-gray-900 dark:text-white placeholder-transparent shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 dark:focus:border-primary-500/70 dark:focus:ring-primary-500/30 transition resize-none"
              />
              <label
                htmlFor="message"
                className="pointer-events-none absolute left-3 top-2 origin-[0] -translate-y-0 text-xs font-medium tracking-wide text-gray-500 dark:text-gray-400 transition-all duration-150 ease-out floating-label"
              >
                Message
              </label>
            </div>

            {feedback && (
              <div className="text-sm text-center text-gray-700 dark:text-gray-300">
                {feedback}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className={`w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 transform hover:scale-[1.02] ${
                submitting ? "opacity-70 cursor-not-allowed" : ""
              }`}
            >
              {submitting ? "Sending..." : "Send Message"}
            </button>
          </form>
        </div>
      ) : (
        <>
          {showHeader && (
            <>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                {heading}
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-8">
                {subheading}
              </p>
            </>
          )}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <FloatingInput
                  id="name"
                  label="Your Name"
                  required
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      name: (e.target as HTMLInputElement).value,
                    }))
                  }
                  placeholder="John Doe"
                />
              </div>

              <div>
                <FloatingInput
                  id="phone"
                  type="tel"
                  label="Phone Number"
                  required
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  placeholder="+91 98765 43210"
                />
                {phoneError && (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                    {phoneError}
                  </p>
                )}
              </div>

              {/* Optional email field (hidden by default on public page) */}
              <div className="hidden">
                <FloatingInput
                  id="email"
                  type="email"
                  label="Email Address"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData((p) => ({
                      ...p,
                      email: (e.target as HTMLInputElement).value,
                    }))
                  }
                  placeholder="john@example.com"
                />
              </div>

              {/* Honeypot field - visually hidden */}
              <div className="sr-only" aria-hidden="true">
                <label htmlFor="company">Company</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  autoComplete="off"
                  tabIndex={-1}
                />
              </div>
            </div>

            <div>
              {/* <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Subject *
            </label> */}
              <Dropdown
                items={subjectItems}
                type="single"
                value={formData.subject}
                onChange={(val) =>
                  setFormData((prev) => ({
                    ...prev,
                    subject: String(val || ""),
                  }))
                }
                placeholder="Select a subject"
                required
                showClear={false}
                className="text-left"
              />
            </div>

            <div className="relative floating-group">
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={6}
                placeholder=" "
                className="peer block w-full rounded-xl border bg-white/70 dark:bg-secondary-800/60 dark:border-secondary-600/60 border-secondary-300/80 px-3 pt-6 pb-2 text-sm text-gray-900 dark:text-white placeholder-transparent shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/40 dark:focus:border-primary-500/70 dark:focus:ring-primary-500/30 transition resize-none"
              />
              <label
                htmlFor="message"
                className="pointer-events-none absolute left-3 top-2 origin-[0] -translate-y-0 text-xs font-medium tracking-wide text-gray-500 dark:text-gray-400 transition-all duration-150 ease-out floating-label"
              >
                Message
              </label>
            </div>

            {feedback && (
              <div className="text-sm text-center text-gray-700 dark:text-gray-300">
                {feedback}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className={`w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 transform hover:scale-[1.02] ${
                submitting ? "opacity-70 cursor-not-allowed" : ""
              }`}
            >
              {submitting ? "Sending..." : "Send Message"}
            </button>
          </form>
        </>
      )}
    </div>
  );
};

export default ContactForm;
